import { useMemo } from "react";
import { useForm } from "react-hook-form";
import type { Error as Auth0Error } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import { useResetPasswordRequestManager } from "../hooks/useResetPasswordRequestManager";
import { getFieldError } from "../utils/helpers/errorUtils";
import { getIdentifierDetails } from "../utils/helpers/identifierUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface ResetPasswordRequestFormData {
  identifier: string;
  captcha?: string;
}

function ResetPasswordRequestScreen() {
  const {
    handleResetPassword,
    handleBackToLogin,
    errors,
    texts,
    isCaptchaAvailable,
    captchaImage,
    allowedIdentifiers,
    requiredIdentifiers,
    prefilledIdentifier,
  } = useResetPasswordRequestManager();

  const availableIdentifiers = useMemo(
    () =>
      requiredIdentifiers.length > 0 ? requiredIdentifiers : allowedIdentifiers,
    [allowedIdentifiers, requiredIdentifiers],
  );
  const identifierDetails = useMemo(
    () => getIdentifierDetails(availableIdentifiers, texts),
    [availableIdentifiers, texts],
  );

  const form = useForm<ResetPasswordRequestFormData>({
    defaultValues: {
      identifier: prefilledIdentifier,
      captcha: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const sdkErrors = (errors || []) as Auth0Error[];
  const generalErrors = sdkErrors.filter((error) => !error.field);
  const identifierSDKError =
    getFieldError("username", sdkErrors) ||
    getFieldError("email", sdkErrors) ||
    getFieldError("phone", sdkErrors) ||
    getFieldError("identifier", sdkErrors);
  const captchaSDKError = getFieldError("captcha", sdkErrors);

  const headingText = texts?.title || "Reset your password";
  const descriptionText =
    texts?.description ||
    "If the email address entered is associated with a valid account, you will receive an email from us with instructions for resetting your password. If you do not receive this email, please check your junk mail folder or contact us for further assistance.";
  const buttonText = texts?.buttonText || "Continue";
  const captchaLabel = (texts?.captchaCodePlaceholder || "CAPTCHA").concat("*");
  const identifierLabel = identifierDetails.label;
  const identifierPlaceholder =
    texts?.identifierPlaceholder ||
    texts?.usernameOrEmailPlaceholder ||
    identifierLabel.replace(/\*$/, "");
  const backToLoginText =
    texts?.backToLoginText || texts?.backText || "Back to sign in";
  const identifierMaxLength = identifierDetails.type === "tel" ? 30 : 128;

  const onSubmit = async (data: ResetPasswordRequestFormData) => {
    await handleResetPassword(data.identifier, data.captcha);
  };

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:block md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              {headingText}
            </p>
            <p className="mt-2 text-sm text-ink-2">{descriptionText}</p>
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`resetpasswordrequest-general-error-${index}`}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ink-2"
              htmlFor="identifier"
            >
              {identifierLabel}
            </label>
            <input
              id="identifier"
              type={identifierDetails.type}
              autoComplete={identifierDetails.autoComplete}
              placeholder={identifierPlaceholder}
              className="input-field"
              aria-invalid={
                !!formErrors.identifier?.message ||
                !!identifierSDKError ||
                undefined
              }
              {...register("identifier", {
                required: "This field is required",
                maxLength: {
                  value: identifierMaxLength,
                  message: `Maximum ${identifierMaxLength} characters allowed`,
                },
              })}
            />
            {(formErrors.identifier?.message || identifierSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.identifier?.message || identifierSDKError}
              </p>
            )}
          </div>

          {isCaptchaAvailable && (
            <div className="space-y-3">
              <label
                htmlFor="captcha"
                className="text-sm font-semibold text-ink-2"
              >
                {captchaLabel}
              </label>
              {captchaImage && (
                <img
                  src={captchaImage}
                  alt="CAPTCHA challenge"
                  className="h-16 w-full rounded-none border border-border object-contain"
                />
              )}
              <input
                id="captcha"
                type="text"
                placeholder={texts?.captchaCodePlaceholder || "Enter the code"}
                className="input-field"
                aria-invalid={
                  !!formErrors.captcha?.message ||
                  !!captchaSDKError ||
                  undefined
                }
                {...register("captcha", {
                  required: "Please complete the CAPTCHA",
                  maxLength: {
                    value: 15,
                    message: "CAPTCHA code is too long",
                  },
                })}
              />
              {(formErrors.captcha?.message || captchaSDKError) && (
                <p className="text-sm text-danger">
                  {formErrors.captcha?.message || captchaSDKError}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          <p className="text-sm text-ink-2">
            Problems signing in?{" "}
            <a
              href="https://www.butterfield.com/my-account-faq"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
            >
              Click here
            </a>
            .
          </p>

          <button
            type="button"
            className="text-sm font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer"
            onClick={() => {
              handleBackToLogin();
            }}
          >
            {backToLoginText}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordRequestScreen;
